import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { isLightColor } from "@ddmarc/shared/lib";

// MSP Branding configuration
export interface BrandingConfig {
  msp_id: number;
  msp_name: string;
  logo_url: string | null;
  primary_color: string;
  accent_color: string;
  support_email: string | null;
  support_url: string | null;
  custom_domain: string | null;
  favicon_url: string | null;
}

interface BrandingContextType {
  branding: BrandingConfig | null;
  isLoading: boolean;
  error: string | null;
  mspSlug: string | null;
}

// Unbranded fallback. Colors mirror the Forest defaults in globals.css
// ([data-ui="v2"]) so the object never contradicts what actually renders when
// no partner branding is loaded (these are only a mapping fallback — the
// visual default lives in the stylesheet, not here). An empty msp_name means
// no partner: PortalBrand then shows the DDMARC mark and name, and the static
// title and favicon in index.html stand.
const defaultBranding: BrandingConfig = {
  msp_id: 0,
  msp_name: "",
  logo_url: null,
  primary_color: "#16A34A",
  accent_color: "#DCFCE7",
  support_email: null,
  support_url: null,
  custom_domain: null,
  favicon_url: null,
};

const BrandingContext = createContext<BrandingContextType>({
  branding: defaultBranding,
  isLoading: true,
  error: null,
  mspSlug: null,
});

// Inject partner colors as [data-ui="v2"] stylesheet rules.
//
// v2 primitives read their palette from custom props declared under the
// [data-ui="v2"] selector in globals.css (with Forest defaults). Setting those
// props inline on <html> does NOT win: a descendant [data-ui="v2"] element
// re-declares them and shadows the inherited value. So we inject rules with the
// SAME selectors globals.css uses — same specificity, appended after it — so
// they win the cascade for every v2 surface.
//
// Critically we emit TWO rules: the base `[data-ui="v2"]` AND the dark selector
// `[data-ui="v2"].dark, .dark [data-ui="v2"]`. The dark selector's specificity
// (0,2,0) out-specifies a bare `[data-ui="v2"]` (0,1,0), so in dark mode (the
// portal default) a base-only override loses to the Forest dark default and the
// partner color never shows.
function applyBrandingColors(config: BrandingConfig) {
  const primary = config.primary_color;
  const accent = config.accent_color;
  const primaryFg = isLightColor(primary) ? "#000000" : "#FFFFFF";
  const accentFg = isLightColor(accent) ? "#000000" : "#FFFFFF";

  const props =
    `--color-primary:${primary};--color-primary-foreground:${primaryFg};` +
    `--color-ring:${primary};--color-sidebar-primary:${primary};` +
    `--color-sidebar-primary-foreground:${primaryFg};--color-chart-1:${primary};` +
    `--color-accent:${accent};--color-accent-foreground:${accentFg};`;

  const css =
    `[data-ui="v2"]{${props}}` +
    `[data-ui="v2"].dark,.dark [data-ui="v2"]{${props}}`;

  let styleEl = document.getElementById(
    "portal-branding"
  ) as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement("style");
    styleEl.id = "portal-branding";
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = css;
}

// Apply favicon + document title from branding. The partner's favicon wins,
// then its logo; with neither, the DDMARC icons in index.html stand. Every
// icon link (touch icon included) is repointed, and its type dropped since a
// partner image need not be SVG, so no DDMARC icon survives on a partner host.
function applyBrandingMeta(config: BrandingConfig) {
  const icon = config.favicon_url || config.logo_url;
  if (icon) {
    document
      .querySelectorAll<HTMLLinkElement>(
        "link[rel='icon'], link[rel='apple-touch-icon']"
      )
      .forEach((link) => {
        link.href = icon;
        link.removeAttribute("type");
      });
  }

  if (config.msp_name) {
    document.title = config.msp_name;
  }
}

// Get MSP slug from URL (subdomain or path)
function getMspSlug(): string | null {
  const hostname = window.location.hostname;

  // Check for subdomain pattern: {msp}.portal.ddmarc.com
  const subdomainMatch = hostname.match(/^([^.]+)\.portal\.ddmarc\.com$/);
  if (subdomainMatch) {
    return subdomainMatch[1];
  }

  // Check for custom domain - will be resolved by API
  if (!hostname.includes("ddmarc.com") && !hostname.includes("localhost")) {
    return hostname;
  }

  return null;
}

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<BrandingConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mspSlug] = useState<string | null>(getMspSlug);

  useEffect(() => {
    async function loadBranding() {
      // If no MSP slug, use defaults (shouldn't happen in portal)
      if (!mspSlug) {
        setBranding(defaultBranding);
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `/api/v1/msp/config?domain=${encodeURIComponent(mspSlug)}`
        );

        if (!response.ok) {
          throw new Error("Failed to load branding configuration");
        }

        // Backend returns MSPConfigResponse: { slug, company_name,
        // custom_domain, branding: {...} | null }. Map it to the flat FE model.
        const raw = await response.json();
        const b = raw.branding ?? null;
        const config: BrandingConfig = {
          msp_id: 0, // backend omits the org id on purpose (uses slug)
          msp_name:
            raw.company_name ?? b?.company_name ?? defaultBranding.msp_name,
          logo_url: b?.logo_url ?? null,
          primary_color: b?.primary_color ?? defaultBranding.primary_color,
          accent_color: b?.accent_color ?? defaultBranding.accent_color,
          support_email: b?.support_email ?? null,
          support_url: b?.support_url ?? null,
          custom_domain: raw.custom_domain ?? null,
          favicon_url: b?.favicon_url ?? null,
        };
        setBranding(config);
        applyBrandingMeta(config);
        // Only override the palette when the partner actually set branding;
        // otherwise the Forest defaults in globals.css stand.
        if (b) {
          applyBrandingColors(config);
        }
      } catch (err) {
        console.error("Failed to load branding:", err);
        setError(err instanceof Error ? err.message : "Unknown error");
        setBranding(defaultBranding);
      } finally {
        setIsLoading(false);
      }
    }

    loadBranding();
  }, [mspSlug]);

  return (
    <BrandingContext.Provider value={{ branding, isLoading, error, mspSlug }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}

export function useMspSlug() {
  const { mspSlug } = useContext(BrandingContext);
  return mspSlug;
}
